/**
 * Making a training plan, and looking at the one you have (B-48).
 *
 * `/plan/new` asks, one question per screen as onboarding does: whichever of build,
 * goals and injuries are still unanswered — required here, where at onboarding they were
 * skippable — then training days, experience and session length. It then builds the plan
 * in the browser (`buildPlan` is pure core), sends it for the injury review only when
 * there are conditions, and shows the result. **Nothing is written until Save**: a plan
 * is a proposal until the person has read it, and the review is a filter the person gets
 * to see the effect of (D-24's rule, applied to a plan instead of a log).
 *
 * `/plan` shows the saved plan with Regenerate and Remove.
 */

import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { BodyType, Experience, FitnessGoal, Health, Plan, SessionMinutes, UserProfile, Weekday } from "@altrtrack/core";
import {
  BODY_TYPES,
  EXERCISE_CAP,
  EXPERIENCES,
  FITNESS_GOALS,
  SESSION_MINUTES,
  buildPlan,
  exerciseById,
  isStepAnswered,
  normaliseHealth,
} from "@altrtrack/core";
import { usePlan, useProfile } from "../data.js";
import { deletePlan, requestReview, savePlan } from "../write.js";
import { Chips, Choices, HealthQuestion } from "../components/Questions.js";

const WEEKDAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
/** Monday first — a training week, the same order `buildPlan` assigns days in. */
const WEEK = [1, 2, 3, 4, 5, 6, 0] as const;

type Step = "bodyType" | "goals" | "health" | "days" | "experience" | "minutes";

const ASKS: Record<Step, { title: string; sub: string }> = {
  bodyType: { title: "Which is closest to your build?", sub: "A plan needs this." },
  goals: { title: "What are you training for?", sub: "The first one you pick sets how many reps you do." },
  health: {
    title: "Any injuries or past surgeries?",
    sub: "Exercises that load an injured area are left out of your plan.",
  },
  days: { title: "Which days do you train?", sub: "Pick two to six." },
  experience: { title: "How much have you trained?", sub: "Decides how many sets, and which exercises come first." },
  minutes: { title: "How long is a session?", sub: "Decides how many exercises fit in a day." },
};

interface Draft {
  bodyType?: BodyType;
  goals: readonly FitnessGoal[];
  health?: Health;
  days: readonly number[];
  experience?: Experience;
  sessionMinutes?: SessionMinutes;
}

export function NewPlanPage({ uid }: { uid: string }) {
  const navigate = useNavigate();
  const profile = useProfile(uid);
  const [draft, setDraft] = useState<Draft>({ goals: [], days: [] });
  const [at, setAt] = useState(0);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  /**
   * The questions to ask, fixed the first time the profile arrives. Re-deriving them on
   * every render would drop the build question out from under the person the moment they
   * answered it — the profile does not change, but a list recomputed from the draft
   * would.
   */
  const steps = useRef<Step[] | null>(null);

  if (profile.data && steps.current === null) {
    const me: UserProfile = profile.data;
    const missing: Step[] = [];
    if (me.bodyType === undefined) missing.push("bodyType");
    if ((me.goals?.length ?? 0) === 0) missing.push("goals");
    if (!isStepAnswered("health", me.health ? { health: me.health } : {})) missing.push("health");
    steps.current = [...missing, "days", "experience", "minutes"];
    // Start from what is already known, including the last plan's preferences.
    const prefs = me.planPrefs;
    setDraft({
      ...(me.bodyType ? { bodyType: me.bodyType } : {}),
      goals: me.goals ?? [],
      ...(me.health ? { health: me.health } : {}),
      days: prefs?.days ?? [],
      ...(prefs ? { experience: prefs.experience, sessionMinutes: prefs.sessionMinutes } : {}),
    });
  }

  if (profile.loading || !steps.current) return <p className="sub">Loading…</p>;

  const list = steps.current;
  const step = list[Math.min(at, list.length - 1)]!;
  const last = at >= list.length - 1;

  const answered =
    step === "bodyType"
      ? draft.bodyType !== undefined
      : step === "goals"
        ? draft.goals.length > 0
        : step === "health"
          ? isStepAnswered("health", draft.health ? { health: draft.health } : {})
          : step === "days"
            ? draft.days.length >= 2 && draft.days.length <= 6
            : step === "experience"
              ? draft.experience !== undefined
              : draft.sessionMinutes !== undefined;

  const build = async () => {
    const built = buildPlan(
      {
        bodyType: draft.bodyType!,
        goals: draft.goals,
        health: normaliseHealth(draft.health!),
        prefs: { days: draft.days, experience: draft.experience!, sessionMinutes: draft.sessionMinutes! },
      },
      Date.now(),
    );
    setPlan(built);
    if (built.review === "pending") await review(built);
  };

  const review = async (toReview: Plan) => {
    setChecking(true);
    // Always review the rules' plan, never an already-reviewed one: a retry after a
    // failure must not compound removals onto a plan the model has already narrowed.
    const reviewed = await requestReview({ ...toReview, review: "pending", removed: [] });
    setChecking(false);
    setPlan(reviewed);
  };

  const forward = () => {
    if (!answered) return;
    if (last) void build();
    else setAt(at + 1);
  };

  const save = async () => {
    if (!plan) return;
    setSaving(true);
    const result = await savePlan(uid, plan);
    setSaving(false);
    if (result.ok) navigate("/profile", { replace: true });
    else setFailure(result.reason === "failed" ? result.detail : "That didn't save.");
  };

  if (plan) {
    return (
      <main className="plan">
        <header className="plan-head">
          <p className="label">Your plan</p>
          <h1 className="title">Check it before you save it</h1>
        </header>

        {checking ? (
          <p className="sub plan-checking" role="status">
            Checking the plan against your injuries…
          </p>
        ) : (
          <>
            <PlanNotices plan={plan} onRetry={() => void review(plan)} />
            <PlanDays plan={plan} />
          </>
        )}

        {failure && (
          <div className="notice bad">
            <b>That didn't save.</b>
            <p className="sub">{failure}</p>
          </div>
        )}

        <div className="onboard-foot">
          <button
            className="btn"
            type="button"
            disabled={saving || checking}
            onClick={() => {
              setPlan(null);
              setAt(0);
            }}
          >
            Start over
          </button>
          <button className="btn btn-primary" type="button" disabled={saving || checking} onClick={() => void save()}>
            {saving ? "Saving…" : "Save plan"}
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="onboard">
      <p className="label onboard-step">
        Step {at + 1} of {list.length}
      </p>
      <h1 className="title">{ASKS[step].title}</h1>
      <p className="sub onboard-sub">{ASKS[step].sub}</p>

      <div className="onboard-body" key={step}>
        {step === "bodyType" && (
          <Choices options={BODY_TYPES} selected={draft.bodyType} onPick={(bodyType) => setDraft((d) => ({ ...d, bodyType }))} />
        )}
        {step === "goals" && (
          <Choices
            options={FITNESS_GOALS}
            selected={draft.goals}
            onPick={(goal) =>
              setDraft((d) => ({
                ...d,
                goals: d.goals.includes(goal) ? d.goals.filter((g) => g !== goal) : [...d.goals, goal],
              }))
            }
          />
        )}
        {step === "health" && (
          <HealthQuestion
            value={draft.health}
            onChange={(update) =>
              setDraft((d) => {
                const health = update(d.health);
                const { health: _old, ...rest } = d;
                return health === undefined ? rest : { ...rest, health };
              })
            }
          />
        )}
        {step === "days" && (
          <>
            <Chips
              label="Training days"
              options={WEEK.map((day) => ({ id: day as number, label: WEEKDAY_NAMES[day]!.slice(0, 3) }))}
              isOn={(day) => draft.days.includes(day)}
              onToggle={(day) =>
                setDraft((d) => ({
                  ...d,
                  days: d.days.includes(day) ? d.days.filter((x) => x !== day) : [...d.days, day],
                }))
              }
            />
            <p className="sub plan-count">
              {draft.days.length} {draft.days.length === 1 ? "day" : "days"} a week
              {draft.days.length > 6 && " — six at most, so there is a rest day"}
            </p>
          </>
        )}
        {step === "experience" && (
          <Choices
            options={EXPERIENCES}
            selected={draft.experience}
            onPick={(experience) => setDraft((d) => ({ ...d, experience }))}
          />
        )}
        {step === "minutes" && (
          <Choices
            options={SESSION_MINUTES.map((m) => ({ id: m, label: `${m} minutes`, blurb: `${EXERCISE_CAP[m]} exercises` }))}
            selected={draft.sessionMinutes}
            onPick={(sessionMinutes) => setDraft((d) => ({ ...d, sessionMinutes }))}
          />
        )}
      </div>

      <div className="onboard-foot">
        {at > 0 ? (
          <button className="btn" type="button" onClick={() => setAt(at - 1)}>
            Back
          </button>
        ) : (
          <Link className="btn" to="/profile">
            Cancel
          </Link>
        )}
        <button className="btn btn-primary" type="button" onClick={forward} disabled={!answered}>
          {last ? "Build my plan" : "Continue"}
        </button>
      </div>
    </main>
  );
}

export function PlanPage({ uid }: { uid: string }) {
  const navigate = useNavigate();
  const plan = usePlan(uid);
  const [confirming, setConfirming] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  if (plan.loading) return <p className="sub">Loading your plan…</p>;

  if (!plan.data) {
    return (
      <main className="plan">
        <h1 className="title">{plan.error === "unreadable" ? "Your saved plan couldn't be read." : "No plan yet."}</h1>
        <p>
          <Link className="btn btn-primary" to="/plan/new">
            {plan.error === "unreadable" ? "Make a new plan" : "Create my plan"}
          </Link>
        </p>
      </main>
    );
  }

  const remove = async () => {
    const result = await deletePlan(uid);
    if (result.ok) navigate("/profile", { replace: true });
    else setFailure(result.reason === "failed" ? result.detail : "That didn't delete.");
  };

  return (
    <main className="plan">
      <header className="plan-head">
        <p className="label">Your plan</p>
        <h1 className="title">
          {Object.keys(plan.data.days).length} days a week
        </h1>
      </header>
      <PlanNotices plan={plan.data} />
      <PlanDays plan={plan.data} />

      {failure && (
        <div className="notice bad">
          <b>That didn't delete.</b>
          <p className="sub">{failure}</p>
        </div>
      )}

      <div className="plan-actions">
        <Link className="btn btn-primary" to="/plan/new">
          Regenerate
        </Link>
        {confirming ? (
          <>
            <button className="btn btn-danger" type="button" onClick={() => void remove()}>
              Delete plan
            </button>
            <button className="btn" type="button" onClick={() => setConfirming(false)}>
              Keep it
            </button>
          </>
        ) : (
          <button className="btn-link-danger" type="button" onClick={() => setConfirming(true)}>
            Remove plan
          </button>
        )}
      </div>
    </main>
  );
}

/** What the person must know before following a plan: the doctor line, and whether the injury check ran. */
function PlanNotices({ plan, onRetry }: { plan: Plan; onRetry?: () => void }) {
  return (
    <>
      {plan.redFlag && (
        <div className="notice">
          <b>Check this plan with your doctor before starting.</b>
          <p className="sub">
            You recorded a condition where hard training carries risk, so high-intensity exercises are left out.
            This plan is not medical advice.
          </p>
        </div>
      )}
      {plan.review === "failed" && (
        <div className="notice bad">
          <b>The injury check didn't run.</b>
          <p className="sub">
            Exercises that load the joints you picked are still left out, but your notes haven't been read.
          </p>
          {onRetry && (
            <button className="btn" type="button" onClick={onRetry}>
              Try again
            </button>
          )}
        </div>
      )}
      {plan.review === "applied" && (
        <p className="sub">Checked against your injury notes. Changes are marked below.</p>
      )}
    </>
  );
}

/** A timed exercise's target is seconds, and says so. */
function target(catalogId: string, targetReps: number): string {
  return exerciseById(catalogId)?.measure === "duration" ? `${targetReps}s` : `${targetReps}`;
}

export function PlanDays({ plan }: { plan: Plan }) {
  return (
    <div className="plan-days">
      {WEEK.filter((day) => plan.days[day as Weekday]).map((day) => {
        const planDay = plan.days[day as Weekday]!;
        return (
          <section className="pcard plan-day" key={day}>
            <h2 className="plan-day-head">
              <span className="plan-day-name">{WEEKDAY_NAMES[day]}</span>
              <span className="label">{planDay.label}</span>
            </h2>
            <ul className="plan-list">
              {planDay.exercises.map((e) => (
                <li key={e.catalogId} className="plan-row">
                  <span className="plan-row-main">
                    <span>{e.name}</span>
                    <span className="num plan-scheme">
                      {e.sets} × {target(e.catalogId, e.targetReps)}
                    </span>
                  </span>
                  {e.review && (
                    <span className="sub">
                      Swapped from {exerciseById(e.review.fromCatalogId)?.name ?? e.review.fromCatalogId} — {e.review.reason}
                    </span>
                  )}
                </li>
              ))}
            </ul>
            {planDay.gaps.map((gap) => (
              <p className="sub" key={gap.muscleGroup}>
                {gap.reason}.
              </p>
            ))}
          </section>
        );
      })}
      {plan.removed.length > 0 && (
        <section className="pcard">
          <h2 className="label pcard-label">Taken out by the injury check</h2>
          <ul className="plan-list">
            {plan.removed.map((r, i) => (
              <li key={`${r.catalogId}-${i}`} className="plan-row">
                <span>{r.name}</span>
                <span className="sub">{r.reason}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
